<template>
  <!-- Hero -->
  <div class="stdtf-hero">
    <v-container>
      <v-row justify="center" class="py-16 text-center">
        <v-col cols="12" md="8">
          <div class="text-overline text-primary mb-3 letter-spacing-wide">
            Free Browser Protection
          </div>
          <h1 class="text-h2 font-weight-black mb-5 hero-headline">
            Stop Scams <span class="text-primary">Before</span><br>They Stop You
          </h1>
          <p class="text-h6 text-medium-emphasis mb-10 hero-sub">
            STDTF tracks
            <strong class="text-primary">
              <template v-if="loading">...</template>
              <template v-else>{{ scamcount.toLocaleString() }}+</template>
            </strong>
            confirmed scam websites and warns you in real time — free, forever, with zero data collection.
          </p>
          <div class="d-flex justify-center gap-4 flex-wrap">
            <v-btn size="x-large" color="primary" variant="elevated" rounded="lg" to="/about">
              How It Works
            </v-btn>
            <v-btn size="x-large" color="primary" variant="outlined" rounded="lg" href="#extensions">
              Get the Extension
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>

  <!-- How It Works -->
  <v-container class="py-16">
    <v-row class="text-center mb-12">
      <v-col>
        <h2 class="text-h4 font-weight-bold">How It Works</h2>
        <p class="text-medium-emphasis mt-2">Three steps. Zero effort. Real protection.</p>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" md="4" class="text-center px-8 mb-8">
        <div class="step-circle mx-auto mb-5">1</div>
        <h3 class="text-h6 font-weight-bold mb-3">Install the Extension</h3>
        <p class="text-medium-emphasis">
          Add STDTF to Firefox, Chrome, or Edge in seconds. Completely free — it collects
          nothing about you or your browsing habits.
        </p>
      </v-col>
      <v-col cols="12" md="4" class="text-center px-8 mb-8">
        <div class="step-circle mx-auto mb-5">2</div>
        <h3 class="text-h6 font-weight-bold mb-3">Browse Normally</h3>
        <p class="text-medium-emphasis">
          The extension runs silently in the background, checking each site you visit against
          our growing database of confirmed scams.
        </p>
      </v-col>
      <v-col cols="12" md="4" class="text-center px-8 mb-8">
        <div class="step-circle mx-auto mb-5">3</div>
        <h3 class="text-h6 font-weight-bold mb-3">Get Warned Instantly</h3>
        <p class="text-medium-emphasis">
          Land on a confirmed scam site and a bold warning appears immediately — before you
          enter any personal information or make any payment.
        </p>
      </v-col>
    </v-row>
  </v-container>

  <!-- Stats Bar -->
  <div class="stdtf-stats-bar">
    <v-container>
      <v-row align="center" justify="space-around" class="py-8">
        <v-col cols="12" md="4" class="text-center">
          <div class="text-h3 font-weight-black text-primary">
            <v-progress-circular v-if="loading" indeterminate size="32" color="primary" />
            <span v-else>{{ scamcount.toLocaleString() }}</span>
          </div>
          <div class="stat-label mt-1">Scam Sites Tracked</div>
        </v-col>
        <v-col cols="12" md="4" class="text-center">
          <div class="text-h3 font-weight-black text-primary">
            <v-progress-circular v-if="loading" indeterminate size="32" color="primary" />
            <span v-else>{{ todayscanned.toLocaleString() }}</span>
          </div>
          <div class="stat-label mt-1">Sites Scanned Today</div>
        </v-col>
        <v-col cols="12" md="4" class="text-center">
          <div class="text-h3 font-weight-black text-primary">
            <v-progress-circular v-if="loading" indeterminate size="32" color="primary" />
            <span v-else>{{ todaycount.toLocaleString() }}</span>
          </div>
          <div class="stat-label mt-1">Scams Added Today</div>
        </v-col>
      </v-row>
    </v-container>
  </div>

  <!-- Extension Downloads -->
  <v-container id="extensions" class="py-16">
    <v-row class="text-center mb-10">
      <v-col>
        <h2 class="text-h4 font-weight-bold">Install the Extension</h2>
        <p class="text-medium-emphasis mt-2">Available for all major browsers — always free.</p>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" md="4" class="mb-4">
        <v-card
          link
          target="_blank"
          href="https://addons.mozilla.org/en-US/firefox/addon/stdtf-site-scanner/"
          class="browser-card text-center py-8"
          rounded="lg"
        >
          <v-icon size="52" class="mb-4" color="primary">mdi-firefox</v-icon>
          <v-card-title class="justify-center text-h6">Firefox</v-card-title>
          <v-card-subtitle>Mozilla Add-ons</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="mb-4">
        <v-card
          link
          target="_blank"
          href="https://chrome.google.com/webstore/detail/scam-takedown-task-force/pgefmbhcbijcdeidlbnokonemgbeogag"
          class="browser-card text-center py-8"
          rounded="lg"
        >
          <v-icon size="52" class="mb-4" color="primary">mdi-google-chrome</v-icon>
          <v-card-title class="justify-center text-h6">Chrome</v-card-title>
          <v-card-subtitle>Chrome Web Store</v-card-subtitle>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" class="mb-4">
        <v-card
          link
          target="_blank"
          href="https://microsoftedge.microsoft.com/addons/detail/scam-takedown-task-force-/hmlcafpldgeflflncbcbpnfjceaaoepj"
          class="browser-card text-center py-8"
          rounded="lg"
        >
          <v-icon size="52" class="mb-4" color="primary">mdi-microsoft-edge</v-icon>
          <v-card-title class="justify-center text-h6">Edge</v-card-title>
          <v-card-subtitle>Microsoft Add-ons</v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Community -->
  <div class="stdtf-community-bar">
    <v-container class="py-12 text-center">
      <h2 class="text-h5 font-weight-bold mb-2">Stay Informed</h2>
      <p class="text-medium-emphasis mb-8">
        Follow us for scam alerts, awareness content, and updates.
      </p>
      <v-row justify="center">
        <v-col cols="12" md="3">
          <v-card
            link
            target="_blank"
            href="https://www.youtube.com/c/ScamTakedownTaskForce"
            variant="flat"
            class="community-card"
          >
            <v-card-text>
              <v-img :src="require('@/assets/YouTube.png')" class="my-2" contain height="48px" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card
            link
            target="_blank"
            href="https://www.facebook.com/STakedownTF"
            variant="flat"
            class="community-card"
          >
            <v-card-text>
              <v-img :src="require('@/assets/Facebook.png')" class="my-2" contain height="48px" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { apiFetch, recordPageVisit } from '../api.js';

const loading = ref(true);
const scamcount = ref(0);
const todayscanned = ref(0);
const todaycount = ref(0);

onMounted(async () => {
  recordPageVisit('Home');
  try {
    const [todayData, scamData, scannedData] = await Promise.all([
      apiFetch('/api/getlatesthits/todaycountdata'),
      apiFetch('/api/getlatesthits/scamcountdata'),
      apiFetch('/api/gettotalscannedtoday'),
    ]);
    todaycount.value = todayData[0].count;
    scamcount.value  = scamData[0].count;
    todayscanned.value = scannedData[0].count;
  } catch {
    // Stats unavailable — leave at 0
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.stdtf-hero {
  background: linear-gradient(160deg, #080c17 0%, #0a1428 50%, #061220 100%);
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
}

.hero-headline {
  line-height: 1.15;
}

.hero-sub {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.letter-spacing-wide {
  letter-spacing: 0.2em;
}

.step-circle {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0077b6, #00b4d8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  box-shadow: 0 4px 20px rgba(0, 180, 216, 0.35);
}

.stdtf-stats-bar {
  background: linear-gradient(90deg, #070b16 0%, #0c1730 50%, #070b16 100%);
  border-top: 1px solid rgba(0, 212, 255, 0.12);
  border-bottom: 1px solid rgba(0, 212, 255, 0.12);
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.browser-card {
  border: 1px solid rgba(0, 212, 255, 0.12) !important;
  background: rgba(15, 21, 37, 0.8) !important;
  transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s !important;
}

.browser-card:hover {
  border-color: rgba(0, 212, 255, 0.45) !important;
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.12) !important;
}

.stdtf-community-bar {
  background: rgba(0, 212, 255, 0.025);
  border-top: 1px solid rgba(0, 212, 255, 0.08);
}

.community-card {
  transition: transform 0.2s, opacity 0.2s !important;
  opacity: 0.8;
}

.community-card:hover {
  transform: translateY(-3px);
  opacity: 1;
}
</style>
